import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  isConnected: false,
  isEstablishingConnection: false,
  isTryingRoom: false,
  userType: "guest",
  sessionType: "",
  sessionStatus: "OFFLINE",
  sessionMsg: "",
  isVr: false,
  isAr: false,
  isTouch: false,
  withAudio: false,
  isSessionMuted: false,
  areGuestsMuted: false,
  amIMuted: false,
  id: "",
  rtpCapabilities: null,
  audioUsers: [],
  roomId: "",
  attendeeId: "",
  availableRooms: null,
  availablePdbs: null,
  trialSlide: "",
  trialScene: null,
  isTyping: false,
  noRoomFound: false,
  isShowingInfoModal: false,
  isShowingOverlay: false,
  isSimOn: true,
  displayToast: false,
  isLlmActive: false,
  toastMsg: "",
  engine: "",
  connectionQuality: {
    status: "idle", // 'idle' | 'testing' | 'passed' | 'failed'
    rtt: null,
    jitter: null,
    lossPercent: null,
    error: null,
  },
};

export const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    startConnecting: (state) => {
      state.isEstablishingConnection = true;
    },
    receivedAvailableRooms: (state, action) => {
      state.availableRooms = action.payload.scenes;
      state.trialSlide = action.payload.trialSlide;
      state.trialScene = action.payload.trialScene;
      state.availablePdbs = action.payload.pdbs;
    },
    receivedStartingPackage: (state, action) => {
      state.withAudio = action.payload.withAudio;
      state.rtpCapabilities = action.payload.rtpCapabilities;
      state.isSessionMuted = action.payload.isSessionMuted;
      state.sessionStatus = "LOADING_SCENE";
      state.roomId = action.payload.roomId;
      state.attendeeId = action.payload.attendeeId || "";
      state.userType = action.payload.isAttendee ? "attendee" : "guest";
      state.sessionType = action.payload.type;
      state.engine = action.payload.engine;
    },
    isVrUser: (state, action) => {
      state.isVr = true;
    },
    isArUser: (state, action) => {
      state.isAr = true;
    },
    isTouchScreen: (state, action) => {
      state.isTouch = true;
    },
    connectionStablished: (state, action) => {
      state.isConnected = true;
      state.isEstablishingConnection = false;
      state.id = action.payload.id;
      state.sessionStatus = "ONLINE";
    },
    connectionLost: (state, action) => {
      state.isConnected = false;
      state.isEstablishingConnection = false;
      state.id = "";
      state.withAudio = false;
      state.isSessionMuted = false;
      state.areGuestsMuted = false;
      state.roomId = "";
      state.availableRooms = null;
      state.sessionStatus = "CONNECTION_LOST";
      state.audioUsers = [];
    },
    sessionClosed: (state, action) => {
      state.withAudio = false;
      state.isSessionMuted = false;
      state.areGuestsMuted = false;
      state.roomId = "";
      state.attendeeId = "";
      state.audioUsers = [];
      state.sessionStatus = action.payload.status;
      state.sessionMsg = action.payload.message;
    },
    toggleAudioChannel: (state, action) => {
      state.withAudio = action.payload.withAudio;
    },
    toggleMute: (state, action) => {
      state.isSessionMuted = action.payload.isSessionMuted;
    },
    toggleMuteGuests: (state, action) => {
      state.areGuestsMuted = action.payload.areGuestsMuted;
    },
    setAdmin: (state, action) => {
      state.userType = "admin";
      state.isShowingInfoModal = true;
    },
    toggleMuteMyself: (state, action) => {
      state.amIMuted = action.payload.amIMuted;
    },
    toggleInfoModal: (state, action) => {
      state.isShowingInfoModal = !state.isShowingInfoModal;
    },
    toggleLlmActive: (state, action) => {
      state.isLlmActive = action.payload.isActive;
    },
    requestTrial: (state, action) => {
      state.isTryingRoom = true;
    },
    leaveTrial: (state, action) => {
      state.isTryingRoom = false;
    },
    isTyping: (state, action) => {
      state.isTyping = action.payload.typing;
      if (action.payload.typing) {
        state.noRoomFound = false;
      }
    },
    noRoomFound: (state, action) => {
      state.noRoomFound = true;
    },
    resetErrors: (state, action) => {
      state.noRoomFound = false;
    },
    toggleHelp: (state, action) => {
      state.isShowingOverlay = !state.isShowingOverlay;
    },
    toggleSimUpdate: (state, action) => {
      state.isSimOn = action.payload.isOn;
    },
    roomLoaded: (state, action) => {
      state.sessionStatus = "ACTIVE";
    },
    tryAgain: (state, action) => {
      state.sessionStatus = "ONLINE";
    },
    minimizing: (state, action) => {
      state.sessionStatus = "MINIMIZING";
    },
    stopMinimizing: (state, action) => {
      state.sessionStatus = "ACTIVE";
    },
    displayToast: (state, action) => {
      state.displayToast = true;
      state.toastMsg = action.payload.message;
    },
    stopDisplayingToast: (state, action) => {
      state.displayToast = false;
      // state.toastMsg = "";
    },
    simUpdated: (state, action) => {
      state.isSimOn = action.payload.isOn;
    },
    qualityTestStarted: (state) => {
      state.connectionQuality = {
        status: "testing",
        rtt: null,
        jitter: null,
        lossPercent: null,
        error: null,
      };
    },
    qualityTestPassed: (state, action) => {
      state.connectionQuality = {
        status: "passed",
        rtt: action.payload.rtt,
        jitter: action.payload.jitter,
        lossPercent: action.payload.lossPercent,
        error: null,
      };
    },
    qualityTestFailed: (state, action) => {
      state.connectionQuality = {
        status: "failed",
        rtt: action.payload.rtt,
        jitter: action.payload.jitter,
        lossPercent: action.payload.lossPercent,
        error: action.payload.reason,
      };
    },
    qualityTestReset: (state) => {
      state.connectionQuality = initialState.connectionQuality;
    },
    emitToggleAudioChannel: (state, action) => {
      return;
    },
    emitToggleMute: (state, action) => {
      return;
    },
    emitToggleMuteGuests: (state, action) => {
      return;
    },
    emitRequestNewRoom: (state, action) => {
      state.sessionStatus = "LOADING_ROOM";
    },
    emitRequestJoinRoom: (state, action) => {
      return;
    },
    emitResetScene: (state, action) => {
      return;
    },
    emitCloseSession: (state, action) => {
      return;
    },
    emitLeaveSession: (state, action) => {
      return;
    },
    emitToggleSim: (state, action) => {
      return;
    },
    emitStartSim: (state, action) => {
      return;
    },
    emitStopSim: (state, action) => {
      return;
    },
    emitSetMolRotationByValue: (state, action) => {
      return;
    },
    emitSetMolRotationChange: (state,action) => {
      return;
    },
    emitWritePDB: (state, action) => {
      return;
    },
  },
});
